// Shared alert-delivery analytics, extracted from alerts_dashboard.vue so
// the dashboard widgets (Alerts By Type, Delivery Status, Recent Delivery
// Errors) classify success/failure and format reasons identically to the
// full Alerts Dashboard page. Keep this in sync with alerts_dashboard.vue's
// own copies of these functions if that page's delivery-status logic ever
// changes.

import moment from 'moment'

// Categorical palette shared by the Alerts By Type widget and the full
// Alerts Dashboard. Fixed order, assigned by volume rank, capped at 8
// series with the rest folded into "Other" (see capSeriesColumns).
// Re-validated 2026-08-01 with the dataviz skill's validate_palette.js:
// the earlier ordering passed the CVD gates but failed the newer
// normal-vision floor on its magenta|orange neighbors (ΔE 12.9 < 15);
// this is the skill's documented default order, which clears every gate
// in both modes (adjacent CVD 9.1 light / 8.4 dark, normal-vision 19.6 /
// 19.3). The sub-3:1 contrast WARNs on the lighter hues are relieved by
// the legend, tooltip, and table view every consumer now renders.
export const TYPE_COLOR_PALETTE = [
  '#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948',
]
// same eight hues stepped for a dark surface -- dark mode is selected,
// never an automatic flip (values from the skill's reference instance)
export const TYPE_COLOR_PALETTE_DARK = [
  '#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#008300', '#9085e9', '#e66767',
]
// muted grey for the folded "Other" series, distinct from all 8 hues above
export const OTHER_TYPE_COLOR = '#9a9994'
export const OTHER_TYPE_COLOR_DARK = '#7c8c9c'

// adapt a gviz-shaped table ([['Date', ...labels], [label, ...values]]) into
// the shared fi_timeline_chart {series, rows} model. series in column order
// take the palette; a folded "Other" column gets the grey.
export function timelineFromGviz(chartData, palette, otherColor) {
  if (!chartData || chartData.length < 2) return null
  const labels = chartData[0].slice(1)
  const series = labels.map((label, i) => ({
    label,
    color: label === 'Other' ? otherColor : palette[i % palette.length],
  }))
  const rows = chartData.slice(1).map(r => ({ label: r[0], values: r.slice(1) }))
  return { series, rows }
}

function getLatestEmailStatus(item) {
  if (!item.email_msg_status || !item.email_msg_status.length) return null
  return item.email_msg_status.reduce((a, b) => (new Date(a.datetime) > new Date(b.datetime) ? a : b))
}

export function isAlertFailed(item) {
  const emailStatus = getLatestEmailStatus(item)
  const hasEmailIssue = emailStatus && ['bounce', 'complaint'].includes(emailStatus.status.toLowerCase())
  const hasSmsIssue = item.provider_response && !item.provider_response.includes('Message has been accepted')
  return !!(hasEmailIssue || hasSmsIssue)
}

// null when the item never attempted that channel, so it's excluded from that channel's counts
export function emailOutcome(item) {
  const emailStatus = getLatestEmailStatus(item)
  if (!emailStatus) return null
  return ['bounce', 'complaint'].includes(emailStatus.status.toLowerCase()) ? 'Email Fail' : 'Email Success'
}

export function smsOutcome(item) {
  if (!item.provider_response) return null
  return item.provider_response.includes('Message has been accepted') ? 'SMS Success' : 'SMS Fail'
}

function decodeEmailStatus(rawStatus) {
  const map = {
    send: 'sent',
    delivery: 'delivered',
    bounce: 'bounced',
    complaint: 'filed complaint',
    open: 'opened',
    click: 'clicked link',
  }
  return map[(rawStatus || '').toLowerCase()] || rawStatus
}

export function failureReason(item) {
  const parts = []
  if (item.provider_response && !item.provider_response.includes('Message has been accepted')) {
    parts.push('sms: ' + item.provider_response)
  }
  const emailStatus = getLatestEmailStatus(item)
  if (emailStatus && ['bounce', 'complaint'].includes(emailStatus.status.toLowerCase())) {
    parts.push('email: ' + decodeEmailStatus(emailStatus.status))
  }
  return parts.join(' | ') || 'unknown'
}

// PascalCase/hyphenated alert types read as words, matching alert_list.vue's
// fallback (e.g. "MissingDVIR-PRE" -> "Missing DVIR PRE")
export function alertTypeLabel(type) {
  return (type || 'Alert')
    .replace(/-/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
}

// [type, count] pairs sorted by volume descending -- the exact distinct
// alert_type breakdown (not the coarser late_alert_cnt/trip_notification_cnt
// buckets calc_daily_stats.py rolls several types into).
export function alertTypeCounts(items) {
  const counts = {}
  items.forEach(item => {
    const type = item.alert_type || 'Unknown'
    counts[type] = (counts[type] || 0) + 1
  })
  return Object.entries(counts).sort((a, b) => b[1] - a[1])
}

// day-by-type stacked/line chart data, identical to alerts_dashboard.vue's
// alertsOverTimeChart computed: a ['Date', ...typeList] header row (types
// ordered by total volume descending, no capping -- the full page doesn't
// cap this series either) followed by one row per day.
export function alertTypesOverTime(items) {
  if (!items.length) return null
  const byDay = {}
  const totalsByType = {}
  items.forEach(item => {
    if (!item.processed_time) return
    const day = moment(item.processed_time).format('YYYY-MM-DD')
    const type = item.alert_type || 'Unknown'
    totalsByType[type] = (totalsByType[type] || 0) + 1
    if (!byDay[day]) byDay[day] = {}
    byDay[day][type] = (byDay[day][type] || 0) + 1
  })
  const days = Object.keys(byDay).sort()
  if (!days.length) return null
  const typeList = Object.entries(totalsByType)
    .sort((a, b) => b[1] - a[1])
    .map(([type]) => type)
  return [
    ['Date', ...typeList],
    ...days.map(day => [moment(day).format('MM/DD'), ...typeList.map(t => byDay[day][t] || 0)]),
  ]
}

// caps a ['Date', ...cols] + rows chart-data table (the shape
// alertTypesOverTime produces) to at most maxCols series, folding any
// columns beyond that into a trailing "Other" column. alertTypesOverTime
// itself stays uncapped (mirrors alerts_dashboard.vue exactly); this is a
// separate, composable step for callers -- like the compact widget -- that
// can't stretch a categorical palette past what's CVD-safe.
export function capSeriesColumns(chartData, maxCols) {
  if (!chartData || chartData.length < 2) return chartData
  const [header, ...rows] = chartData
  const cols = header.slice(1)
  if (cols.length <= maxCols) return chartData
  const newHeader = ['Date', ...cols.slice(0, maxCols), 'Other']
  const newRows = rows.map(row => {
    const kept = row.slice(1, maxCols + 1)
    const otherTotal = row.slice(maxCols + 1).reduce((sum, v) => sum + (Number(v) || 0), 0)
    return [row[0], ...kept, otherTotal]
  })
  return [newHeader, ...newRows]
}

// aggregate totals across the whole item set (not per-day, unlike the full
// Alerts Dashboard's time-series version) -- right-sized for a compact
// dashboard widget's summary tiles.
export function deliveryOutcomeCounts(items) {
  const totals = { 'Email Success': 0, 'Email Fail': 0, 'SMS Success': 0, 'SMS Fail': 0 }
  items.forEach(item => {
    const eo = emailOutcome(item)
    if (eo) totals[eo]++
    const so = smsOutcome(item)
    if (so) totals[so]++
  })
  return totals
}

export function recentFailures(items, limit = 5) {
  return items
    .filter(isAlertFailed)
    .slice()
    .sort((a, b) => new Date(b.processed_time) - new Date(a.processed_time))
    .slice(0, limit)
    .map(item => ({
      key: item.movement_vehicle_id || (item.alert_type + '-' + item.processed_time),
      alert_type: item.alert_type,
      vehicle_id: item.vehicle_id || item.movement_vehicle_id || '',
      reason: failureReason(item),
      processed_time: item.processed_time,
    }))
}
